/**
 * Fabric arrival inspections — measured-on-arrival actuals (GSM / width /
 * shrinkage / shade) for a fabric item, read against the master spec with an
 * off-spec flag. Append-only log + a record form. Rendered under the master
 * spec on the garment-gated Fabric specs tab.
 *
 * Shrinkage is shown/entered as a percent but stored as basis points (3% ⇄ 300).
 *
 * @see packages/domain/src/garment/fabric-inspections
 */
import { useState } from "react";
import { Plus, TriangleAlert, X } from "lucide-react";
import { Button, Input, toast } from "@flint/ui";
import type { fabricInspections as inspContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { FormSelect } from "../../styles/components/form-select.js";

type View = inspContract.ItemInspectionsWire;
type Row = View["inspections"][number];
type Status = Row["status"];

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "passed", label: "Passed" },
  { value: "flagged", label: "Flagged" },
  { value: "hold", label: "On hold" },
];

interface Props {
  itemId: string;
}

export function FabricInspectionsSection({ itemId }: Props) {
  const utils = trpc.useUtils();
  const query = trpc.fabricInspections.forItem.useQuery({ itemId });
  const [adding, setAdding] = useState(false);

  const record = trpc.fabricInspections.record.useMutation({
    onSuccess: async () => {
      await utils.fabricInspections.forItem.invalidate({ itemId });
      setAdding(false);
      toast.success("Inspection recorded");
    },
    onError: (e: { message: string }) => toast.error("Couldn't record", { description: e.message }),
  });

  if (query.isLoading || !query.data) return null;
  const { master, inspections } = query.data;

  return (
    <div className="panel">
      <div className="panel-head">
        <h3>Arrival inspections</h3>
        {!adding ? (
          <button type="button" className="tag-add" onClick={() => setAdding(true)}>
            <Plus size={10} aria-hidden /> Record
          </button>
        ) : (
          <button type="button" className="tag-add" onClick={() => setAdding(false)}>
            <X size={10} aria-hidden /> Cancel
          </button>
        )}
      </div>
      <div className="panel-body">
        {adding ? (
          <InspectionForm master={master} pending={record.isPending} onSave={(v) => record.mutate({ itemId, ...v })} />
        ) : null}

        {inspections.length === 0 ? (
          !adding ? (
            <p className="desc" style={{ color: "var(--muted)", fontStyle: "italic" }}>
              No arrivals inspected yet. Record measured GSM, width, shrinkage and shade when a fabric lot is received —
              anything off the master spec is flagged.
            </p>
          ) : null
        ) : (
          <div className="flex flex-col gap-2">
            {inspections.map((r) => (
              <InspectionRow key={r.id} row={r} master={master} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── row ────────────────────────────────────────────────────────

function InspectionRow({ row, master }: { row: Row; master: View["master"] }) {
  return (
    <div className={`rounded-md border p-2.5 ${row.deviatesFromSpec ? "border-amber-200 bg-amber-50" : "border-line bg-bg"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <StatusChip status={row.status} />
          <span className="font-medium text-ink">{row.lotRef ?? "—"}</span>
          <span className="text-muted">{fmtDate(row.inspectedOn)}</span>
          {row.deviatesFromSpec ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-warn">
              <TriangleAlert size={11} aria-hidden /> off-spec
            </span>
          ) : null}
        </div>
        {row.inspector ? <span className="text-xs text-muted">{row.inspector}</span> : null}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
        <Measured label="GSM" value={row.measuredGsm} spec={master?.gsm ?? null} unit="" />
        <Measured label="Width" value={row.measuredWidthCm} spec={master?.widthCm ?? null} unit=" cm" />
        <Measured
          label="Shrinkage"
          value={row.measuredShrinkageBp === null ? null : row.measuredShrinkageBp / 100}
          spec={master?.shrinkageBp == null ? null : master.shrinkageBp / 100}
          unit="%"
        />
        {row.shade ? (
          <span>
            <span className="text-muted">Shade</span> <span className="font-mono">{row.shade}</span>
          </span>
        ) : null}
      </div>
      {row.notes ? <p className="mt-1 text-xs text-muted">{row.notes}</p> : null}
    </div>
  );
}

function Measured({ label, value, spec, unit }: { label: string; value: number | null; spec: number | null; unit: string }) {
  if (value === null) return null;
  return (
    <span>
      <span className="text-muted">{label}</span>{" "}
      <span className="font-mono">
        {value}
        {unit}
      </span>
      {spec !== null ? (
        <span className="text-muted">
          {" "}
          (spec {spec}
          {unit})
        </span>
      ) : null}
    </span>
  );
}

function StatusChip({ status }: { status: Status }) {
  const tone =
    status === "passed"
      ? "bg-emerald-50 text-ok"
      : status === "flagged"
        ? "bg-amber-50 text-warn"
        : "bg-red-50 text-bad";
  const label = status === "passed" ? "Passed" : status === "flagged" ? "Flagged" : "Hold";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${tone}`}>{label}</span>;
}

// ── form ───────────────────────────────────────────────────────

interface SaveInput {
  lotRef: string | null;
  measuredGsm: number | null;
  measuredWidthCm: number | null;
  measuredShrinkageBp: number | null;
  shade: string | null;
  status: Status;
  inspector: string | null;
  inspectedOn: string;
}

function InspectionForm({ master, pending, onSave }: { master: View["master"]; pending: boolean; onSave: (v: SaveInput) => void }) {
  const [lotRef, setLotRef] = useState("");
  const [gsm, setGsm] = useState("");
  const [widthCm, setWidthCm] = useState("");
  const [shrinkagePct, setShrinkagePct] = useState("");
  const [shade, setShade] = useState("");
  const [status, setStatus] = useState<Status>("passed");
  const [inspector, setInspector] = useState("");
  const [inspectedOn, setInspectedOn] = useState(() => new Date().toISOString().slice(0, 10));

  return (
    <div className="mb-3 rounded-md border border-line bg-bg p-3">
      {master ? (
        <p className="mb-2 text-xs text-muted">
          Master spec — GSM {master.gsm ?? "—"} · Width {master.widthCm ?? "—"} cm · Shrinkage{" "}
          {master.shrinkageBp === null ? "—" : `${master.shrinkageBp / 100}%`}
        </p>
      ) : (
        <p className="mb-2 text-xs text-muted">No master spec set — deviations won't be flagged.</p>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Lot / roll ref">
          <Input value={lotRef} onChange={(e) => setLotRef(e.currentTarget.value)} placeholder="LOT-2451-A" className="h-8 w-36 text-xs" />
        </Field>
        <Field label="GSM">
          <Input value={gsm} onChange={(e) => setGsm(e.currentTarget.value)} placeholder="180" inputMode="numeric" className="h-8 w-20 text-xs" />
        </Field>
        <Field label="Width (cm)">
          <Input value={widthCm} onChange={(e) => setWidthCm(e.currentTarget.value)} placeholder="150" inputMode="numeric" className="h-8 w-20 text-xs" />
        </Field>
        <Field label="Shrinkage (%)">
          <Input value={shrinkagePct} onChange={(e) => setShrinkagePct(e.currentTarget.value)} placeholder="3" inputMode="decimal" className="h-8 w-20 text-xs" />
        </Field>
        <Field label="Shade">
          <Input value={shade} onChange={(e) => setShade(e.currentTarget.value)} placeholder="Lot 2 · +½" className="h-8 w-28 text-xs" />
        </Field>
        <Field label="Result">
          <FormSelect
            ariaLabel="Result"
            value={status}
            onValueChange={(v) => setStatus(v as Status)}
            options={STATUS_OPTIONS}
            triggerClassName="h-8 w-28 px-2 text-xs"
          />
        </Field>
        <Field label="Inspector">
          <Input value={inspector} onChange={(e) => setInspector(e.currentTarget.value)} placeholder="Store QA" className="h-8 w-28 text-xs" />
        </Field>
        <Field label="Date">
          <input
            type="date"
            value={inspectedOn}
            onChange={(e) => setInspectedOn(e.currentTarget.value)}
            className="h-8 rounded-sm border border-line bg-paper px-2 font-mono text-xs text-ink outline-none focus-visible:border-sky-600"
          />
        </Field>
        <Button
          size="sm"
          disabled={pending}
          onClick={() =>
            onSave({
              lotRef: lotRef.trim() || null,
              measuredGsm: parseIntOrNull(gsm),
              measuredWidthCm: parseIntOrNull(widthCm),
              measuredShrinkageBp: parsePercentToBp(shrinkagePct),
              shade: shade.trim() || null,
              status,
              inspector: inspector.trim() || null,
              inspectedOn,
            })
          }
        >
          {pending ? "Saving…" : "Save inspection"}
        </Button>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-muted">{label}</span>
      {children}
    </label>
  );
}

function parseIntOrNull(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Math.round(Number(t));
  return Number.isFinite(n) ? n : null;
}

function parsePercentToBp(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}
